import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';

/**
 * A etapa de PROTOCOLO: as receptoras escolhidas e o cronograma hormonal.
 *
 * Mesmo desenho da TE — uma sessão por serviço, duas listas filhas, tudo numa
 * chamada só de RPC. Enfileirável porque isto é lançado em campo, na fazenda,
 * que é onde o sinal falta.
 */

export const fetchProtocoloSession = (serviceId) =>
  cached(`protocolo-session:${serviceId}`, () => run(supabase
    .from('protocolo_sessions')
    .select('*')
    .eq('service_id', serviceId)
    .maybeSingle()));

export const fetchProtocoloReceptoras = (sessionId) =>
  cached(`protocolo-receptoras:${sessionId}`, () => run(supabase
    .from('protocolo_receptoras')
    .select('*')
    .eq('protocolo_session_id', sessionId)
    .order('sort_order')));

export const fetchProtocoloAplicacoes = (sessionId) =>
  cached(`protocolo-aplicacoes:${sessionId}`, () => run(supabase
    .from('protocolo_aplicacoes')
    .select('*')
    .eq('protocolo_session_id', sessionId)
    .order('sort_order')));

export const saveProtocoloSession = queueable('protocolo.save',
  (params) => run(supabase.rpc('save_protocolo_session', params)),
  {
    pendingOf: (params, appData) => appData && {
      kind: 'protocolo',
      id: params.p_service_id,
      data: appData,
      label: 'Sessão de protocolo',
    },
  });

/**
 * O que está na tela vira o corpo do RPC.
 *
 * Separada do hook de propósito: é a parte que tem regra, e é a que dá para
 * testar sem subir Supabase nenhum — mesma razão de `montarPayloadTE` não
 * existir e de o teste da numeração olhar `src/data/embryos.js`.
 *
 * `sort_order` sai do índice do array porque é a ordem em que a equipe
 * escreveu os brincos, e é a ordem em que o produtor assina o relatório.
 */
export const montarPayloadProtocolo = (serviceId, laboratoryId, dados = {}) => {
  const { session = {}, receptoras = [], aplicacoes = [], observations, completed } = dados;

  return {
    p_service_id: serviceId,
    p_laboratory_id: laboratoryId,
    p_session: {
      date: session.date || null,
      farm: session.farm || null,
      city: session.city || null,
      technician_id: session.technician_id || null,
      technician_name: session.technician_name || null,
      protocol_name: session.protocol_name || null,
      observations: observations || null,
      completed: !!completed,
      completed_at: completed ? (dados.completed_at || new Date().toISOString()) : null,
    },
    // A linha em branco do fim da tabela não é receptora. O RPC também a
    // descarta; aqui ela sai antes para não virar um `sort_order` perdido.
    p_receptoras: receptoras
      .filter(r => (r.identifier || '').trim() || r.animal_id)
      .map(r => ({
        animal_id: r.animal_id || null,
        identifier: (r.identifier || '').trim() || null,
        breed: r.breed || null,
        // Vazio vira null, não '': o RPC faz `::numeric` e '' estouraria.
        ecc: (r.ecc === '' || r.ecc === null || r.ecc === undefined) ? null : r.ecc,
        ativa: r.ativa !== false,
        observations: r.observations || null,
      })),
    p_aplicacoes: aplicacoes
      .filter(a => (a.atividade || '').trim())
      .map(a => ({
        data: a.data || null,
        atividade: (a.atividade || '').trim(),
        // Dose é TEXTO: "1,00 ML" e "1 UND" convivem na mesma coluna.
        dose: (a.dose || '').trim() || null,
        responsavel: a.responsavel || null,
        momento: a.momento || 'inicio',
      })),
  };
};

/** A sessão do banco volta ao formato que a tela usa. */
export const sessaoParaTela = (sessao, receptoras = [], aplicacoes = []) => ({
  _session_id: sessao.id,
  session: {
    date: sessao.session_date,
    farm: sessao.farm,
    city: sessao.city,
    technician_id: sessao.technician_id,
    technician_name: sessao.technician_name,
    protocol_name: sessao.protocol_name,
  },
  receptoras: (receptoras || []).map(r => ({
    _db_id: r.id,
    id: r.id,
    animal_id: r.animal_id,
    identifier: r.identifier || '',
    breed: r.breed || '',
    ecc: r.ecc,
    ativa: r.ativa !== false,
    observations: r.observations || '',
  })),
  aplicacoes: (aplicacoes || []).map(a => ({
    _db_id: a.id,
    id: a.id,
    data: a.data,
    atividade: a.atividade || '',
    dose: a.dose || '',
    responsavel: a.responsavel || '',
    momento: a.momento || 'inicio',
  })),
  observations: sessao.observations || '',
  completed: sessao.completed,
  completed_at: sessao.completed_at,
});
